import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { formatCurrency, formatDate } from '@/lib/format'
import { formatEnumValue } from '@/lib/formatters'
import { cn } from '@/lib/utils'

const HEADER_BG =
  'color-mix(in srgb, var(--primary) 8%, var(--background))' as const

const DEFAULT_PREVIEW_LIMIT = 5

export type EquityTransactionRow = {
  id: string
  date: string
  fundAbbreviation: string
  fundDisplayName: string
  type: string
  subType: string | null
  amount: number
}

export function EquityTransactionsRegister({
  rows,
  isLoading,
  previewLimit = DEFAULT_PREVIEW_LIMIT,
}: {
  rows: EquityTransactionRow[]
  isLoading?: boolean
  previewLimit?: number
}) {
  const [expanded, setExpanded] = useState(false)

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full rounded-sm" />
        <Skeleton className="h-10 w-full rounded-sm" />
      </div>
    )
  }

  if (rows.length === 0) return null

  const hasMore = rows.length > previewLimit
  const visibleRows = expanded || !hasMore ? rows : rows.slice(0, previewLimit)
  const remaining = rows.length - previewLimit

  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-sm border border-primary/20 bg-white">
        <div className="overflow-auto">
          <Table
            disableWrapper
            className="[&_th]:border-primary/20 [&_td]:border-primary/20 [&_th:first-child]:border-l-0 [&_td:first-child]:border-l-0 [&_th:last-child]:border-r-0 [&_td:last-child]:border-r-0 [&_tbody_tr:last-child_td]:border-b-0"
          >
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead
                  style={{ background: HEADER_BG }}
                  className="min-w-[120px]"
                >
                  Date
                </TableHead>
                <TableHead
                  style={{ background: HEADER_BG }}
                  className="min-w-[100px]"
                >
                  Fund
                </TableHead>
                <TableHead
                  style={{ background: HEADER_BG }}
                  className="min-w-[160px]"
                >
                  Type
                </TableHead>
                <TableHead
                  style={{ background: HEADER_BG }}
                  className="min-w-[140px] text-right"
                >
                  Amount
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleRows.map((row) => (
                <TableRow key={row.id} className="hover:bg-primary/5">
                  <TableCell className="tabular-nums text-primary/80">
                    {formatDate(row.date)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className="text-[11px] font-medium tracking-wide"
                    >
                      {row.fundAbbreviation || row.fundDisplayName}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-primary/80">
                    {row.subType ?? formatEnumValue(row.type)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-primary/80">
                    {formatCurrency(row.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {hasMore ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="group flex w-full cursor-pointer items-center justify-center gap-1.5 border-t border-primary/20 py-2 text-primary/70 transition-colors hover:bg-primary/[0.03] hover:text-primary"
            aria-expanded={expanded}
          >
            <Typography
              as="span"
              variant="body-sm"
              className="text-[11px] font-medium uppercase tracking-[0.08em]"
            >
              {expanded ? 'Show fewer' : `Show ${remaining} more`}
            </Typography>
            <ChevronDown
              className={cn(
                'h-3 w-3 transition-transform duration-200',
                expanded && 'rotate-180'
              )}
            />
          </button>
        ) : null}
      </div>
    </div>
  )
}
